import AccordionItem from "../blocks/accordion-item";

// Gets the yyyy-MM-dd prefix from JSON. Prevents timezone conversion
function formatFilteredEventDateRange(startDateValue: unknown, endDateValue: unknown): string | null {
    const parseCalendarPartsFromCmsString = (cmsDateValue: unknown) => {
        const trimmedDateStringPrefix = String(cmsDateValue ?? "").trim().slice(0, 10);
        const hyphenSeparatedDateParts = trimmedDateStringPrefix.split("-");
        if (hyphenSeparatedDateParts.length !== 3) return null;
        const calendarYear = Number(hyphenSeparatedDateParts[0]);
        const calendarMonth = Number(hyphenSeparatedDateParts[1]);
        const calendarDay = Number(hyphenSeparatedDateParts[2]);
        if (Number.isNaN(calendarYear) || Number.isNaN(calendarMonth) || Number.isNaN(calendarDay)) {
            return null;
        }
        if (
            calendarYear <= 1900 ||
            calendarMonth < 1 ||
            calendarMonth > 12 ||
            calendarDay < 1 ||
            calendarDay > 31
        ) {
            return null;
        }
        return { year: calendarYear, month: calendarMonth, day: calendarDay };
    };

    const formatShortMonthWithTitleCase = (calendarYear: number, calendarMonth: number) => {
        const shortMonthFromLocale = new Date(calendarYear, calendarMonth - 1, 1).toLocaleDateString(
            undefined,
            { month: "short" },
        );
        return shortMonthFromLocale
            ? shortMonthFromLocale.charAt(0).toUpperCase() + shortMonthFromLocale.slice(1).toLowerCase()
            : shortMonthFromLocale;
    };

    const startCalendarParts = parseCalendarPartsFromCmsString(startDateValue);
    const endCalendarParts = parseCalendarPartsFromCmsString(endDateValue);
    if (!startCalendarParts || !endCalendarParts) return null;

    const startDayNumberText = String(startCalendarParts.day);
    const endDayNumberText = String(endCalendarParts.day);
    const eventStartMonthLabel = formatShortMonthWithTitleCase(
        startCalendarParts.year,
        startCalendarParts.month,
    );
    const eventEndMonthLabel = formatShortMonthWithTitleCase(
        endCalendarParts.year,
        endCalendarParts.month,
    );
    const eventStartYearText = String(startCalendarParts.year);
    const eventEndYearText = String(endCalendarParts.year);
    const eventYearRangeLabel =
        eventStartYearText === eventEndYearText
            ? eventStartYearText
            : `${eventStartYearText} - ${eventEndYearText}`;

    if (eventStartMonthLabel === eventEndMonthLabel) {
        return startDayNumberText === endDayNumberText
            ? `${eventStartMonthLabel} ${startDayNumberText}, ${eventYearRangeLabel}`
            : `${eventStartMonthLabel} ${startDayNumberText}-${endDayNumberText}, ${eventYearRangeLabel}`;
    }
    return `${eventStartMonthLabel} ${startDayNumberText} - ${eventEndMonthLabel} ${endDayNumberText}, ${eventYearRangeLabel}`;
}

export default function FilterPage() {
    const mainContent = document.getElementById('main-content')
    const filterModal = document.getElementById('filterModal')
    const filterBtn = document.getElementById('filter-Btn');
    const modalBackBtn = document.getElementById('back-btn');
    const isOrganizationPage = document.getElementById('OrganizationPage')
    const isSearchPage = document.getElementById('SearchPage')
    let useDefaultFilters = true;
    var totalPages = document.getElementById('resultsContainer')?.getAttribute('data-total-pages');
    let isMobile = false;
    let currentPage = 1;

    function setIsMobile() {
        if(window.innerWidth >= 993) {
            isMobile = false
            setRemoveAllFiltersButtonVisibility()
        } else {
            isMobile = true
            setRemoveAllFiltersButtonVisibility()
        }
    }

    setIsMobile()

    window.addEventListener('resize', () => {
        setIsMobile()
        // Rebuild pagination window on Search when layout changes
        const paginationEl = document.getElementById('pagination')
        if (paginationEl && isSearchPage && totalPages && parseInt(totalPages) > 1) {
            initPaginationButtons()
        }
    })
    
    // Function to restore filter state from URL parameters on page load
    function restoreFiltersFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        
        const searchInput = mainContent?.querySelector<HTMLInputElement>('input[type="text"]');
        const searchQuery = urlParams.get('q');
        if (searchInput && searchQuery) {
            searchInput.value = decodeURIComponent(searchQuery);
        }
        
        const filterParam = urlParams.get('f');
        if (filterParam) {
            const filterValues = filterParam.split(',');
            filterValues.forEach(value => {
                const checkbox = document.querySelector<HTMLInputElement>(`input.filter-chk-box[value="${value}"]`);
                if (checkbox) {
                    checkbox.checked = true;
                }
            });
        }
        
        const sortParam = urlParams.get('s');
        if (sortParam) {
            const sortInput = mainContent?.querySelector<HTMLInputElement>('.jsSortbyFilter');
            const sortBy = mainContent?.querySelector('#sortBy');
            if (sortInput && sortBy) {
                sortInput.value = sortParam;
                sortBy.setAttribute('data-sort-by', sortParam);
            }
        }
        
        const pageParam = urlParams.get('p');
        if (pageParam) {
            currentPage = parseInt(pageParam) || 1;
        }

        const defaultParam = urlParams.get('default');
        if (defaultParam !== null) {
            useDefaultFilters = defaultParam !== 'false';
        }
        
        createFilterboxItems();
        setRemoveAllFiltersButtonVisibility();
        
        document.querySelectorAll('.filter-group').forEach(group => {
            const clearBtn = group.querySelector('.jsClearCategoryMobile');
            if (clearBtn) setClearCategoryButtonVisibility(group, clearBtn);
        });
        
        // Update pagination if it exists
        if (pagination) {
            initPaginationButtons();
        }
    }
    
    // Call restore function on page load
    restoreFiltersFromUrl();

    function setMobileFilterScrollLock(isOpen: boolean) {
        const shouldLock = isOpen && window.innerWidth < 1024;
        document.body.style.overflow = shouldLock ? 'hidden' : '';
    }

    function positionMobileFilterModal(isOpen: boolean) {
        if (!filterModal) return;

        if (isOpen && window.innerWidth < 1024) {
            const header = document.querySelector('header');
            const top = header instanceof HTMLElement ? header.offsetHeight : 56;
            filterModal.style.top = `${top}px`;
        } else {
            filterModal.style.top = '';
        }
    }

    function toggleHideMobileFilters() {
        var filtersVisible = mainContent?.getAttribute('data-open-filter');
        
        if(filtersVisible === 'closed') {            
            mainContent?.setAttribute('data-open-filter', 'open')
            filterModal?.removeAttribute('inert')
            setMobileFilterScrollLock(true)
            positionMobileFilterModal(true)
        } else if (filtersVisible === 'open') {
            mainContent?.setAttribute('data-open-filter', 'closed')
            filterModal?.setAttribute('inert', '')
            setMobileFilterScrollLock(false)
            positionMobileFilterModal(false)
        } 
    }
    
    //closes modal going from mobile to desktop
    window.addEventListener('resize', () => {
        if(window.innerWidth > 1024) {
            mainContent?.setAttribute('data-open-filter', 'closed')
            filterModal?.removeAttribute('inert')
            setMobileFilterScrollLock(false)
            positionMobileFilterModal(false)
        } else if (mainContent?.getAttribute('data-open-filter') === 'open') {
            positionMobileFilterModal(true)
        }
    })

    if(filterBtn) {
        filterBtn.addEventListener('click', toggleHideMobileFilters)
    }

    if(modalBackBtn) {
        modalBackBtn.addEventListener('click', toggleHideMobileFilters)
    }

    function showHideLoadMoreButton() {
        const loadMoreButton = document.getElementById('Loadmore');
        
        if(totalPages && (currentPage >= parseInt(totalPages))) {
            if(loadMoreButton) {
                loadMoreButton.style.display = 'none';
            }
        } else {
            if(loadMoreButton)  loadMoreButton.style.display = 'block'
        }

    }

    showHideLoadMoreButton()

    async function fetchResults(url?: string) {
        try {
            // Use the provided URL or fall back to current location
            const fetchUrl = url || window.location.href;
            const res = await fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' }});
            
            const html = await res.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newResults = doc.querySelector('#resultsContainer');
            const newTotalPages = newResults?.getAttribute('data-total-pages');
            const newCount = doc.querySelector('#resultsCount');
            const newPagination = doc.querySelector('#pagination');
            const resultsHost = document.querySelector('#resultsContainer');
            const countHost = document.querySelector('#resultsCount');
            const paginationHost = document.querySelector('#pagination');
            
            if (newResults && resultsHost) resultsHost.innerHTML = newResults.innerHTML;            
            if (newCount && countHost) countHost.innerHTML = newCount.innerHTML;
            if (newPagination && paginationHost) paginationHost.innerHTML = newPagination.innerHTML;
            
            if (newTotalPages) {
                totalPages = newTotalPages;
                resultsHost?.setAttribute('data-total-pages', totalPages)
            };
            
            currentPage = 1;
            
            showHideLoadMoreButton();
            if(paginationHost) initPaginationButtons();
        
            if(isOrganizationPage != null) AccordionItem()
        } catch (e) {
            console.log(e, 'POST Error');
            
        }
    }

    // Wire up Loadmore button using event delegation so it works after DOM updates
    document.addEventListener('click', (e) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;
        const loadMoreButton = target.closest('#Loadmore') as HTMLElement | null;
        if (loadMoreButton) {
            e.preventDefault();
            loadMore();
        }
    });

    let loadMoreInProgress = false;

    async function loadMore() {
        if (loadMoreInProgress) {
            return;
        }

        const nextPage = currentPage + 1;
        const container = mainContent || document;
        const queryInput = container.querySelector<HTMLInputElement>('input[type="text"]');
        const q = queryInput?.value || '';
        const checked = Array.from(document.querySelectorAll<HTMLInputElement>('input.filter-chk-box:checked'));
        const filterValues = checked.map(chk => chk.value).filter(Boolean);
        const sortInput = container.querySelector<HTMLInputElement>('.jsSortbyFilter');
        const s = sortInput?.value.trim();

        const resultsContainer = document.querySelector('#resultsContainer') || document;
        const loadedIds = Array.from(resultsContainer.querySelectorAll<HTMLAnchorElement>('a[data-content-id]'))
            .map(anchor => anchor.getAttribute('data-content-id') || '')
            .map(id => Number(id))
            .filter(id => Number.isFinite(id));

        const formPayload = new URLSearchParams();
        if (q) formPayload.append('q', q);
        if (filterValues.length > 0) formPayload.append('f', filterValues.join(','));
        formPayload.append('p', String(nextPage));
        if (s) formPayload.append('s', s);
        formPayload.append('default', String(useDefaultFilters));
        loadedIds.forEach(id => formPayload.append('loadedIds', String(id)));

        const basePath = window.location.pathname.replace(/\/$/, '');
        const url = `${basePath}/LoadMore`;

        const button = document.getElementById('Loadmore');

        loadMoreInProgress = true;
        if (button) {
            button.classList.add('opacity-50', 'pointer-events-none');
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: formPayload.toString()
            });
            if (!response.ok) return;
            const newResults = await response.json();
            
            appendResults(newResults);
            currentPage = nextPage;

            showHideLoadMoreButton();

        } catch (err) {
            console.error('Load more failed', err);
        } finally {
            loadMoreInProgress = false;
            if (button) {
                button.classList.remove('opacity-50', 'pointer-events-none');
            }
        }
    }

    function appendResults(newResults: any[]) {
        const filteredEvents = document.querySelector('.filtered-events') || document.querySelector('#resultsContainer');
        if (!filteredEvents) return;

        const existingIds = new Set(
            Array.from(filteredEvents.querySelectorAll<HTMLAnchorElement>('a[data-content-id]')).map(anchor => anchor.getAttribute('data-content-id') || '')
        );
        const existingLinks = new Set(
            Array.from(filteredEvents.querySelectorAll<HTMLAnchorElement>('a[href]')).map(anchor => anchor.getAttribute('href') || '')
        );

        // Remove existing button while appending
        const btn = filteredEvents.querySelector('#Loadmore');
        if (btn) btn.remove();
        newResults.forEach((result: any) => {
            // Prefer dedupe by content id if available, otherwise fallback to href
            if (result.contentId && existingIds.has(String(result.contentId))) {
                return;
            }
            if (result.contentId) existingIds.add(String(result.contentId));

            if (!result.pageLink || existingLinks.has(result.pageLink)) {
                return;
            }
            existingLinks.add(result.pageLink);

            let eventLocationToShow = '';
            if (result.location) {
                eventLocationToShow = result.location;
            } else if (result.categoryNames && result.categoryNames.length > 0) {
                if (result.categoryNames.includes('Live')) {
                    eventLocationToShow = 'FREE VIRTUAL EVENT';
                } else if (result.categoryNames.includes('On-Demand Webinar')) {
                    eventLocationToShow = 'FREE ON-DEMAND WEBINAR';
                }
            }

            let EventDays: string | null = null;
            const isOnDemand = result.categoryNames && result.categoryNames.includes('On-Demand Webinar');
            
            if (result.startDate && result.endDate && !isOnDemand) {
                EventDays = formatFilteredEventDateRange(result.startDate, result.endDate);
            }

            const publishDateDisplay = result.publishDateDisplay || '';

            const html = `
                <a href="${result.pageLink || '#'}" data-content-id="${result.contentId || ''}" class="relative h-full lg:min-h-[252px] col-span-1 rounded-3xl overflow-hidden flex flex-col lg:flex-row shadow-md no-underline hover:cursor-pointer hover:shadow-lg global-transition">
                    ${result.featured ? `
                        <div class="bg-primary-300 text-white rounded-br-[40px] space-x-[6px] absolute left-0 top-0 flex py-2 px-[18px] items-center">
                            <span class="font-base font-semibold leading-6 -tracking-[0.9px]">Featured</span>
                            <span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                    <mask id="mask0_10340_116661" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="18" height="18">
                                        <rect width="18" height="18" fill="#D9D9D9"/>
                                        </mask>
                                        <g mask="url(#mask0_10340_116661)">
                                        <path d="M4.36875 15.75L5.5875 10.4813L1.5 6.9375L6.9 6.46875L9 1.5L11.1 6.46875L16.5 6.9375L12.4125 10.4813L13.6312 15.75L9 12.9563L4.36875 15.75Z" fill="white"/>
                                    </g>
                                </svg>
                            </span>
                        </div>` : ''
                    }
                    ${result.imageUrl ? `
                        <div class="w-full max-h-[405px] lg:w-[389px] lg:max-h-[310px] overflow-hidden shrink-0">
                            <img src="${result.imageUrl}" alt="" class="object-cover h-full w-full" loading="lazy" width="298" height="252" />
                        </div>
                    ` : ''}
                    <div class="flex w-full flex-col space-y-4 py-6 px-4 lg:space-y-4 md:p-8 ${result.featured ? 'bg-neutral-200' : ''}">
                        <div class="flex flex-col">
                            ${eventLocationToShow || EventDays || publishDateDisplay ? 
                                `<div class="flex justify-between">
                                    ${eventLocationToShow ? `<span class="text-xs font-normal leading-6 tracking-[-0.18px] text-type-50 uppercase">${eventLocationToShow}</span>` : ''}
                                    ${EventDays !== null ? `<span class="text-xs font-normal leading-6 tracking-[-0.18px] text-type-50 uppercase">${EventDays}</span>` : ''}
                                    ${publishDateDisplay ? `<span class="text-xs font-normal leading-6 tracking-[-0.18px] text-type-50 uppercase ml-auto">${publishDateDisplay}</span>` : ''}
                                </div>` : ''}
    
                            ${result.title ? `<h4 class="text-type-100 line-clamp-2 text-[22px] leading-[27px] font-semibold -tracking-[0.45px] lg:line-clamp-none">${result.title}</h4>` : ''} 
                        </div>
                        ${result.description ? ` <div class="text-base text-black font-normal line-clamp-3 leading-6 -tracking-[0.24px]">${result.description}</div>` : ''}
                    </div>
                </a>
            `;
            filteredEvents.insertAdjacentHTML('beforeend', html);
        });
    }

    //Build and navigate with query params (q, f, p, s, d)
    function applyFilters(page: number = 1, resetAll?: boolean, ignoreDefaultFilters?: boolean ) {
       const container = mainContent || document;
       const queryInput = container.querySelector<HTMLInputElement>('input[type="text"]');
       const q = resetAll ? '' : (queryInput?.value || '').trim();
        const checked = resetAll ? [] : Array.from(document.querySelectorAll<HTMLInputElement>('input.filter-chk-box:checked'));
        const filterValues = checked.map(chk => chk.value).filter(Boolean);
       const sortInput = container.querySelector<HTMLInputElement>('.jsSortbyFilter');
       const s = resetAll ? '' : (sortInput?.value || '').trim();
       
       if(ignoreDefaultFilters) {
        useDefaultFilters = false;
       }

        // Manually build query string to avoid comma-separated IDs
        const urlParams: string[] = [];

        if (!resetAll) {
           if (q) urlParams.push(`q=${encodeURIComponent(q)}`);
            if (filterValues.length > 0) urlParams.push(`f=${filterValues.join(',')}`);
            if (page && page > 1) urlParams.push(`p=${String(page)}`);
           if (s) urlParams.push(`s=${encodeURIComponent(s)}`);
           urlParams.push(`default=${useDefaultFilters}`);
        } else {
            urlParams.push(`default=${useDefaultFilters}`);
        }
        const query = urlParams.length > 0 ? `?${urlParams.join('&')}` : '';
        
        const newUrl = `${location.pathname}${query}`;
        
        window.history.replaceState(null, '', newUrl);
        
        fetchResults(newUrl);
    }

    //SortByToggle button
    var sortBy = mainContent?.querySelector('#sortBy')
    var sortByOptions = sortBy?.querySelectorAll('.js-sort-by-option')
    var inputValue = sortBy?.querySelector('.jsSortbyFilter');

    function toggleSortBy(sortOption: Element) {       
        let sortOptionValue = sortOption.getAttribute('data-value')

        if(sortOptionValue === 'atoz') {
            sortBy?.setAttribute('data-sort-by', 'atoz')
            inputValue?.setAttribute('value', 'atoz')
        } else {
            sortBy?.setAttribute('data-sort-by', 'newest')
            inputValue?.setAttribute('value', 'newest')
        }
        // Trigger filtering immediately on desktop
        if (!isMobile) applyFilters(1);
    }

    sortByOptions?.forEach((option) => {
        option.addEventListener('click', () => toggleSortBy(option))
       
    })
    
    function createFilterboxItems() {
        const filterBoxMobile = document.querySelector(".jsFilterBoxMobile");
    
        if (filterBoxMobile) filterBoxMobile.innerHTML = "";
    
        // FILTERS BOX
        document.querySelectorAll('#mobile-filters-form .filter-chk-box:checked').forEach(input => {
            
            const label = input.closest("label.ind-cat");
            const txt = label ? label.textContent?.trim() : "";
            const val = input.getAttribute("value");
            if (filterBoxMobile) {
                filterBoxMobile.insertAdjacentHTML(
                    "afterbegin",
                    `<span role="button" class="filter-item btn-chip bg-neutral-300 py-2 px-4 -tracking-[0.21px] w-fit rounded-[36px] font-semibold hover:bg-accent1-50 focus:bg-accent1-50">${txt} 
                        <svg class="icon-close hover:cursor-pointer" data-filter-val="${val}" aria-label="Remove ${val} filter" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M6.28033 5.21967C5.98744 4.92678 5.51256 4.92678 5.21967 5.21967C4.92678 5.51256 4.92678 5.98744 5.21967 6.28033L8.93934 10L5.21967 13.7197C4.92678 14.0126 4.92678 14.4874 5.21967 14.7803C5.51256 15.0732 5.98744 15.0732 6.28033 14.7803L10 11.0607L13.7197 14.7803C14.0126 15.0732 14.4874 15.0732 14.7803 14.7803C15.0732 14.4874 15.0732 14.0126 14.7803 13.7197L11.0607 10L14.7803 6.28033C14.0732 5.98744 14.0732 5.51256 14.7803 5.21967C14.4874 4.92678 14.0126 4.92678 13.7197 5.21967L10 8.93934L6.28033 5.21967Z" fill="#0F172A"/>
                        </svg>
                    </span>`
                );
            }
        });
    
        // bind close button click handlers
        document.querySelectorAll('.filter-box .icon-close').forEach(icon => {
            icon.addEventListener("click", function (this: HTMLElement) {
                const val = this.getAttribute("data-filter-val");
    
                const input = document.querySelector(`input.filter-chk-box[value="${val}"]`);
                if (input) (input as HTMLInputElement).checked = false; 
                
                document.querySelectorAll("#filterModal .filter-group").forEach(group => {
                   const btn = group.querySelector('.jsClearCategoryMobile');
                   if (btn) setClearCategoryButtonVisibility(group, btn);
                });

                if (this.parentElement) {
                    this.parentElement.remove();
                }
    
                // hide 'remove all' button if no active filters
                setRemoveAllFiltersButtonVisibility();

                let filterButtonLength = document.querySelectorAll('.filter-box .icon-close').length
                
                if (!isMobile && filterButtonLength >= 1) {
                    // Navigate with updated filters
                    applyFilters(1);
                } else {
                    applyFilters(1, false, true);
                }
            });
        });
    
        setRemoveAllFiltersButtonVisibility();
    }
    
    createFilterboxItems();


    function setRemoveAllFiltersButtonVisibility() {
        const removeAllFiltersButtonMobile = document.querySelector(".jsRemoveAllFiltersMobile");
        const removeAllFiltersButtonDesktop = document.querySelector(".jsRemoveAllFilters")

        const hasFilters = document.querySelectorAll("#filterModal .filter-item").length > 0;
        
        if (!removeAllFiltersButtonMobile || !removeAllFiltersButtonDesktop) return;

        (removeAllFiltersButtonMobile as HTMLElement).setAttribute(
            "style",
            hasFilters  && isMobile ? "display: block !important" : "display: none !important"
        );

        removeAllFiltersButtonDesktop.setAttribute('style', `display: ${hasFilters && !isMobile ? 'block' : 'none'} !important`);    
    
    }

    document.querySelectorAll('input.filter-chk-box + label').forEach(label => {
        label.addEventListener('click', function (e) {
            e.preventDefault();

            const checkbox = label.previousElementSibling;
    
            if (checkbox && (checkbox as HTMLInputElement).type === "checkbox") {
                // toggle checked state
                (checkbox as HTMLInputElement).checked = !(checkbox as HTMLInputElement).checked;
    
                // trigger "change" event manually
                checkbox.dispatchEvent(new Event("change", { bubbles: true }));
            }
            createFilterboxItems();
    
            if (isMobile) {
                const filterGroup = label.closest('.filter-group');
                if (filterGroup) {
                    const clearBtn = filterGroup.querySelector('.jsClearCategoryMobile');
                    if (clearBtn) setClearCategoryButtonVisibility(filterGroup, clearBtn);
                }
            } else {
                // Desktop: navigate on change
                applyFilters(1, false, true);
            }
        });
    })

    // Add event listeners to checkboxes themselves to handle direct clicks
    document.querySelectorAll('input.filter-chk-box').forEach(checkbox => {
        checkbox.addEventListener('change', function(this: HTMLInputElement) {
            
            createFilterboxItems();
            // Update clear category button visibility
            const filterGroup = this.closest('.filter-group');
            if (filterGroup) {
                const clearBtn = filterGroup.querySelector('.jsClearCategoryMobile');
                if (clearBtn) setClearCategoryButtonVisibility(filterGroup, clearBtn);
            }
            
            // Update remove all filters button visibility
            setRemoveAllFiltersButtonVisibility();
            
            if (!isMobile) {
                // Desktop: navigate on change
                applyFilters(1, false, true);
            }
        });
    });

    // Delegated listeners as a safety net (desktop)
    document.addEventListener('change', (e) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;
        if (target.matches && target.matches('input.filter-chk-box')) {
            const inMobileForm = !!target.closest('#mobile-filters-form');
            if (!inMobileForm) {
                applyFilters(1);
            }
        }
    });

    document.addEventListener('click', (e) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;
        const label = target.closest('label.ind-cat');
        if (label) {
            const inMobile = !!label.closest('#filterModal');
            if (!inMobile) {
                // Let the direct handlers toggle state, then apply
                setTimeout(() => applyFilters(1), 0);
            }
        }
    });

    document.querySelectorAll('.filter-group').forEach(group => {
        const clearCategoryButtons = group.querySelectorAll('.jsClearCategory, .jsClearCategoryMobile');
    
        clearCategoryButtons.forEach(clearCategoryButton => {
            clearCategoryButton.addEventListener('click', function (e) {
                e.preventDefault();
                let clearCnt = 0;

                //checkboxes inside .panel-collapse
                const filterGroupEl = clearCategoryButton.closest('.filter-group');
                const collapseSection = filterGroupEl?.querySelector('.panel-collapse');
                if (collapseSection && collapseSection.classList.contains('panel-collapse')) {
                    collapseSection.querySelectorAll('input.filter-chk-box:checked').forEach(chk => {
                        const val = chk.getAttribute('value');
                        document.querySelectorAll(`.filter-box .icon-close[data-filter-val="${val}"]`).forEach(icon => {
                            if (icon.parentElement) {
                                icon.parentElement.remove();
                            }
                        });
                        (chk as HTMLInputElement).checked = false;
                        clearCnt++;
                    });
                }

                // checkboxes inside .filter-checkboxes-first-3
                const first3Section = filterGroupEl?.querySelector('.filter-checkboxes-first-3');
                if (first3Section && first3Section.classList.contains('filter-checkboxes-first-3')) {
                    first3Section.querySelectorAll('input.filter-chk-box:checked').forEach(chk => {
                        const val = chk.getAttribute('value');
                        document.querySelectorAll(`.filter-box .icon-close[data-filter-val="${val}"]`).forEach(icon => {
                            if (icon.parentElement) {
                                icon.parentElement.remove();
                            }
                        });
                        (chk as HTMLInputElement).checked = false;
                        clearCnt++;
                    });
                }
                
                const filterGroup = clearCategoryButton.closest('.filter-group');
                if (filterGroup) {
                    setClearCategoryButtonVisibility(filterGroup, clearCategoryButton);
                }
                setRemoveAllFiltersButtonVisibility();
               

                if (!isMobile && clearCnt > 0) {
                    // Desktop: navigate on change
                    applyFilters(1, false, true);
                }
            });
        });
    
        // Initial clear button visibility
        clearCategoryButtons.forEach(btn => {
            setClearCategoryButtonVisibility(group, btn);
        });
    });

    function setClearCategoryButtonVisibility(filterGroup: Element, clearCategoryButton: Element) {
        const checkedInputs = filterGroup.querySelectorAll('input.filter-chk-box:checked');
        if (checkedInputs.length > 0) {
            (clearCategoryButton as HTMLElement).style.display = 'flex';
        } else {
            (clearCategoryButton as HTMLElement).style.display = 'none';
        }
    }

    document.querySelectorAll('.jsRemoveAllFilters, .jsRemoveAllFiltersMobile')
        .forEach(button => {
            button.addEventListener('click', function (e) {
                e.preventDefault();

                // Uncheck all checkboxes
                document.querySelectorAll('#mobile-filters-form .filter-chk-box:checked')
                    .forEach(chk => { (chk as HTMLInputElement).checked = false; });

                // Remove all filter items
                document.querySelectorAll('#filterModal .filter-box .filter-item')
                    .forEach(item => item.remove());

                // Hide remove all + clear category buttons (mobile)
                document.querySelectorAll('.jsRemoveAllFiltersMobile, .jsClearCategoryMobile')
                    .forEach(el => { (el as HTMLElement).style.display = 'none'; });

                // Clear search input
                const searchInput = mainContent?.querySelector<HTMLInputElement>('input[type="text"]');
                if (searchInput) searchInput.value = '';

                setRemoveAllFiltersButtonVisibility()

                // Update URL with no params
                applyFilters(1, true, true);
            });
        });

    // Show more/less toggle for filter groups
    function initShowMoreToggles() {
        const showMoreLinks = document.querySelectorAll<HTMLAnchorElement>('.show-more-block');
        if (!showMoreLinks || showMoreLinks.length === 0) return;

        showMoreLinks.forEach(link => {
            const targetSelector = link.getAttribute('href') || '';
            const panel = targetSelector ? document.querySelector<HTMLElement>(targetSelector) : null;
            const showMoreLabel = link.querySelector<HTMLElement>('.show-more');
            const showLessLabel = link.querySelector<HTMLElement>('.show-less');

            const setState = (isOpen: boolean) => {
                link.setAttribute('aria-expanded', String(isOpen));
                panel?.setAttribute('aria-expanded', String(isOpen));

                if (isOpen) {
                    link.classList.remove('collapsed');
                    if (panel) panel.style.display = 'block';
                    if (showMoreLabel) showMoreLabel.style.display = 'none';
                    if (showLessLabel) showLessLabel.style.display = 'inline';
                } else {
                    link.classList.add('collapsed');
                    if (panel) panel.style.display = 'none';
                    if (showMoreLabel) showMoreLabel.style.display = 'inline';
                    if (showLessLabel) showLessLabel.style.display = 'none';
                }
            };

            // Initialize state based on aria-expanded or collapsed class
            const ariaExpanded = (link.getAttribute('aria-expanded') || '').trim().toLowerCase();
            const initialOpen = ariaExpanded === 'true' || !link.classList.contains('collapsed');
            setState(initialOpen);

            link.addEventListener('click', (e) => {
                e.preventDefault();
                const current = (link.getAttribute('aria-expanded') || '').trim().toLowerCase() === 'true';
                setState(!current);
            });
        });
    }

    initShowMoreToggles();

    // Mobile: Apply button triggers navigation with current filters
    document.querySelectorAll('.mobile-filters-submit').forEach(button => {
        button.addEventListener('click', () => {
            applyFilters(1);
            toggleHideMobileFilters()
        });
    });

    // Search input handlers (enter key and button click)
    const searchInput = mainContent?.querySelector<HTMLInputElement>('input[type="text"]');
    const searchButton = searchInput?.parentElement?.querySelector('button');
    if (searchInput) {
        searchInput.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                applyFilters(1, false, true);
            }
        });
        if(isSearchPage) {
            if(searchButton && searchInput.value && searchInput.value.length > 0) {
                searchButton.style.display = 'flex'; 
            }

            searchInput.addEventListener('input', () => {
                if (searchButton && searchInput.value && searchInput.value.length > 0) {   
                    searchButton.style.display = 'flex';   
                } else if (searchButton) {
                    searchButton.style.display = 'none';
                }
            })
        }
    }
    if (searchButton && !isSearchPage) {
        searchButton.addEventListener('click', (e) => {
            e.preventDefault();
            applyFilters(1);
        });
    } else if(searchButton && isSearchPage) {
        searchButton.addEventListener('click', (e) => {
            e.preventDefault();
            searchButton.style.display = 'none'
           if(searchInput) searchInput.value = '';
           applyFilters(1)
        })
    }
    
    var pagination = document.getElementById('pagination');
    function initPaginationButtons() {
        var queryString = window.location.search;
        var urlParams = new URLSearchParams(queryString);
        var urlParamsPage = urlParams.get('p') || '1';
        
        const currentPagination = document.getElementById('pagination');
        let currentPaginationBtns = currentPagination?.querySelectorAll('.page-btn');
        
        if (!currentPagination) return;

        if(currentPagination && totalPages && parseInt(totalPages) > 1) {
            currentPagination.style.display = "flex";
        } else {
            currentPagination.style.display = 'none';
            return;
        }
       
        const tPages = parseInt(totalPages || '1');
        const active = parseInt(urlParamsPage || String(currentPage));
        const buttonsHost = ensureSearchButtonsHost(currentPagination as HTMLElement);
        buttonsHost.innerHTML = buildPaginationButtons(active, tPages, !!isSearchPage);
        // re-query after rebuild
        currentPaginationBtns = currentPagination?.querySelectorAll('.page-btn');
        

        currentPaginationBtns?.forEach((btn) => {
            const page = parseInt(btn.getAttribute('data-page') || "1");
            
            if(!isSearchPage) {
                if(urlParamsPage && parseInt(urlParamsPage) === page) {
                    currentPagination?.setAttribute('data-active-page', page.toString())
                    currentPage = page
                    btn.classList.add('bg-secondary-500', 'text-white');
                } else {
                    btn.classList.remove('bg-secondary-500', 'text-white');
                }
            } else {
                if(urlParamsPage && parseInt(urlParamsPage) === page) {
                    currentPagination?.setAttribute('data-active-page', page.toString())
                    currentPage = page
                    btn.classList.add('border-t-2', 'border-t-secondary-300');
                } else {
                    btn.classList.remove('border-t-2', 'border-t-secondary-300');
                }

            }

        });

        setPaginationArrowVisiblity(urlParamsPage ? parseInt(urlParamsPage) : currentPage)

    }

    initPaginationButtons()
    
    let lastSelectedPage = 1;
    function setSelectedPaginationButton(btn?: Element, nextPage?: number, apply?: boolean) {
        let page = parseInt(btn?.getAttribute('data-page') || "1");
    
        if (nextPage) page = nextPage;
        
        const currentPagination = document.getElementById('pagination');
        const currentPaginationBtns = currentPagination?.querySelectorAll('.page-btn');
    
        if(!isSearchPage) {
            currentPaginationBtns?.forEach((button) => {
                button.classList.remove('bg-secondary-500', 'text-white');
                button.classList.add('text-black');
            });
        } else {
            currentPaginationBtns?.forEach((button) => {
                button.classList.remove('border-t-2', 'border-t-secondary-300');
            });
        }
    
        if (!btn && nextPage) {
            btn = Array.from(currentPaginationBtns || []).find(
                (b) => b.getAttribute('data-page') === nextPage.toString()
            ) as Element | undefined;
        }
    
        if (btn && !isSearchPage) {
            btn.classList.add('bg-secondary-500', 'text-white');
        } else if (btn && isSearchPage) {
            btn.classList.add('border-t-2', 'border-t-secondary-300');
        }
    
        if (page !== lastSelectedPage && apply) {
            applyFilters(page, false);
            lastSelectedPage = page;
            currentPagination?.setAttribute('data-active-page', page.toString());
            currentPage = page
            setPaginationArrowVisiblity(currentPage)
        }
    }
    
    // Use event delegation for pagination buttons to handle dynamic content
    document.addEventListener('click', (e) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;
        
        if (target.matches && target.matches('.page-btn')) {
            setSelectedPaginationButton(target, undefined, true);
        }
    });

    function ensureSearchButtonsHost(paginationEl: HTMLElement) {
        let paginationButtonHost = paginationEl.querySelector('.js-pagination-buttons') as HTMLElement | null;

        if (!paginationButtonHost) {
            const children = Array.from(paginationEl.children) as HTMLElement[];
            let middle = children.find(el => !el.classList.contains('pagination-arrow-prev') && !el.classList.contains('pagination-arrow-next') && el.tagName.toUpperCase() !== 'BUTTON') as HTMLElement | undefined;
            if (middle) {
                middle.classList.add('js-pagination-buttons');
                paginationButtonHost = middle;
            } else {
                const firstDirectBtn = children.find(el => el.classList.contains('page-btn')) as HTMLElement | undefined;
                if (firstDirectBtn) {
                    const wrapper = document.createElement('div');
                    wrapper.className = 'js-pagination-buttons';
                    paginationEl.insertBefore(wrapper, firstDirectBtn);
                    // Move all direct child page buttons into the wrapper
                    Array.from(paginationEl.children).forEach(node => {
                        const n = node as HTMLElement;
                        if (n.classList && n.classList.contains('page-btn')) {
                            wrapper.appendChild(n);
                        }
                    });
                    paginationButtonHost = wrapper;
                }
            }
        }

        // If still no paginationButtonHost, create one before the Next arrow to preserve layout
        if (!paginationButtonHost) {
            paginationButtonHost = document.createElement('div');
            paginationButtonHost.className = 'js-pagination-buttons';
            const nextArrow = paginationEl.querySelector('.pagination-arrow-next');
            if (nextArrow && nextArrow.parentElement === paginationEl) {
                paginationEl.insertBefore(paginationButtonHost, nextArrow);
            } else {
                paginationEl.appendChild(paginationButtonHost);
            }
        }

        // Clear the paginationButtonHost before injecting new buttons
        paginationButtonHost.innerHTML = '';
        return paginationButtonHost;
    }

    function buildPaginationButtons(activePage: number, total: number, isSearch: boolean) {
        const parts: string[] = [];
        const pushBtn = (p: number) => {
            if (isSearch) {
                parts.push(`<button data-page="${p}" class="page-btn p-4 text-type-50 text-xs font-semibold leading-5 tracking-[0.1px] border-t-2 global-transition ${p === activePage ? ' border-t-secondary-300' : 'border-t-secondary-300/0'}">${p}</button>`);
            } else {
                parts.push(`<button data-page="${p}" class="page-btn rounded-lg px-3 py-[6px] text-black text-base font-semibold leading-[22px] tracking-[-0.24px] global-transition ${p === activePage ? ' bg-secondary-500' : 'bg-secondary-500/0'}">${p}</button>`);
            }
        };

        let numberWindowLimit;
        if (isSearch) {
            numberWindowLimit = isMobile ? 3 : 9;
        } else {
            numberWindowLimit = 5;
        }

        if (total <= numberWindowLimit) {
            for (let i = 1; i <= total; i++) pushBtn(i);
            return parts.join('');
        }
     
        const halfNumberWindow = Math.floor(numberWindowLimit / 2);
        const maxStart = Math.max(1, total - (numberWindowLimit - 1));
        let start = Math.max(1, Math.min(activePage - halfNumberWindow, maxStart));
        let end = Math.min(total, start + (numberWindowLimit - 1));

        for (let p = start; p <= end; p++) pushBtn(p);
        return parts.join('');
    }

    function setPaginationArrowVisiblity(updatedcurrentPage: number){
        const currentPagination = document.getElementById('pagination');
        const currentPaginationArrowNext = currentPagination?.querySelector('.pagination-arrow-next');
        const currentPaginationArrowPrev = currentPagination?.querySelector('.pagination-arrow-prev');
        const total = parseInt(totalPages || '1');
        
        if(updatedcurrentPage >= total) {
            currentPaginationArrowNext?.classList.add('invisible')
            currentPaginationArrowNext?.classList.remove('visible')
        } else {
            currentPaginationArrowNext?.classList.add('visible')
            currentPaginationArrowNext?.classList.remove('invisible')
        }

        if(updatedcurrentPage == 1) {
            currentPaginationArrowPrev?.classList.add('invisible')
            currentPaginationArrowPrev?.classList.remove('visible')
        } else {
            currentPaginationArrowPrev?.classList.add('visible')
            currentPaginationArrowPrev?.classList.remove('invisible')
        }
    }
    
    document.addEventListener('click', (e) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;
        
        const nextButton = target.closest('.pagination-arrow-next');
        const prevButton = target.closest('.pagination-arrow-prev');
        
        if (nextButton) {
            const total = parseInt(totalPages || '1');
            if (currentPage < total) {
                setSelectedPaginationButton(undefined, currentPage + 1, true);
            }
        }
        
        if (prevButton) {
            if (currentPage > 1) {
                setSelectedPaginationButton(undefined, currentPage - 1, true);
            }
        }
    });
}
